/**
 * Projection for `CustomersScene` — the demo's `renderCustomers()` (line 2744)
 * and the `notifications()` builder it shares with the S.O.S report (line 2736).
 *
 * Pure: no React, no fetching, no engine mutation. Every figure is read off the
 * loaded payloads or the eager core bundle; this file only joins, filters and
 * orders.
 *
 * ## The organisation model
 *
 * The demo carries four small constants at line 1481–1486 that never made it
 * into `oem-engine.ts`, because nothing in Tier 1 needed them:
 *
 * - `ORG_OF_MINE` — the six owner organisations, one per site.
 * - `CONTRACTOR_ORG` — the three underground contractors, by the site they work.
 * - `CUST_CHANNEL` — how each customer wants to be told things.
 * - `orgOfPerson` — the directory spells two contractors longer than the org
 *   register does, so a person's `org` has to be normalised before it joins.
 *
 * They are defined here and exported, because this is the surface that owns the
 * customer organisation as a concept. A later lane that needs them can import
 * them from here rather than restating the map.
 *
 * ## One declared deviation
 *
 * The demo resolves a contractor's fleet by matching `Machine.operated_by`
 * against the org name, remapping **only** `'Spinifex Underground'` →
 * `'Spinifex Underground Contracting'`. The fleet register spells the third
 * contractor `'Western Tiers Mining Services'` while the org register spells it
 * `'Western Tiers Mining'`, so the demo's Western Tiers card reports a fleet of
 * zero. {@link CONTRACTOR_FLEET_NAME} remaps both, which is what the demo's own
 * `orgOfPerson` does for the people join one line earlier. Nothing outside this
 * page reads the figure.
 *
 * Transformation layer (layer 3 of the 4-layer architecture).
 *
 * @see src/versions/v1/modules/customers/CustomersScene.tsx
 * @see docs/logic/customers/customers.md
 * @see docs/foundations/4-layer-architecture.md
 */

import { orgs as ORGS } from '../../../../../data/oem/brand.json';
import { BY_ID, CONTRACTORS, FLEET, MINES, MINE_ORDER } from '../../data/core';
import type { Machine, MineCode } from '../../schema/machine';
import { ATTENTION_BANDS } from '../../schema/machine';
import type { Alert } from '../../schema/alert';
import { ALERTS } from '../../shared/seed/alerts';
import { INCIDENTS } from '../../shared/seed/incidents';
import { statusOf } from '../../shared/oem-engine';
import { DATA_OEM_ORG } from '../../shared/docs/doc-identity';
import type {
  Bulletin,
  CustomerSatisfactionResponse,
  PartOrder,
  PersonRecord,
  ServiceCase,
  TechnicianJob,
  TechnicianPresence,
  WorkOrder,
} from '../../shared/useOemData';

// ---------------------------------------------------------------------------
// The organisation register
// ---------------------------------------------------------------------------

/** One organisation's letterhead details — a value of `brand.json` `orgs`. */
export interface OrgRecord {
  readonly legal: string;
  readonly unit: string;
  readonly addr: string;
  readonly phone: string;
  readonly web: string;
  /** Corporate colour, used as an inline swatch. Data, not a design token. */
  readonly colour: string;
  readonly ink: string;
}

/** The ten organisations in the register, keyed by display name. */
export const ORG_REGISTER: Readonly<Record<string, OrgRecord>> = ORGS;

/**
 * The OEM itself, as the data files spell it. Excluded from the customer list
 * — it is the reader.
 *
 * This is a join key, not a label: `people.json`, `sites.json` and the org
 * register all say "Meridian Equipment" whichever brand is selected, so the
 * value must not follow the brand. Anything that DISPLAYS the OEM's name takes
 * it as a parameter instead (see {@link oemOrgLabel}), and the scene passes
 * the selected brand's name from `useOemDocIdentity()`.
 */
export const OEM_ORG = DATA_OEM_ORG;

/**
 * An organisation's display name: the data's OEM spelling becomes the
 * selected brand's name (`oemName`), every other org is shown as-is. Pure.
 */
export const oemOrgLabel = (org: string, oemName: string): string =>
  org === OEM_ORG ? oemName : org;

/** Demo line 1481. The owner organisation at each site. */
export const ORG_OF_MINE: Readonly<Record<MineCode, string>> = {
  KG: 'Kalbarri Gold',
  BR: 'Broken Ridge',
  MI: 'Mount Isolde Copper',
  OH: 'Ochre Hill Gold',
  GD: 'Gawler Deep',
  MR: 'Mount Read Metals',
};

/** Demo line 1483. The underground contractor at each site that has one. */
export const CONTRACTOR_ORG: Readonly<Partial<Record<MineCode, string>>> = {
  MI: 'Redgum Mining Services',
  GD: 'Spinifex Underground',
  MR: 'Western Tiers Mining',
};

/** Demo line 2735. How each customer wants to be told things. */
export const CUST_CHANNEL: Readonly<Record<string, string>> = {
  'Kalbarri Gold': 'Email (Craig) · Teams for the planner',
  'Broken Ridge': 'Teams channel · email digest 06:30',
  'Mount Isolde Copper': 'Email · seismic events by SMS',
  'Redgum Mining Services': 'Teams channel (Redgum planning)',
  'Ochre Hill Gold': 'Email · SMS for parts landing',
  'Gawler Deep': 'Teams channel · Maximo notifications',
  'Spinifex Underground': 'Email · SMS to the supervisor',
};

/** The channel for an org, defaulting the way the demo does. */
export const channelFor = (org: string): string => CUST_CHANNEL[org] ?? 'Email';

/**
 * Demo line 1486. `orgOfPerson` — the directory's spelling of two contractors
 * is longer than the org register's. Normalise before joining on org.
 */
const PERSON_ORG_ALIAS: Readonly<Record<string, string>> = {
  'Spinifex Underground Contracting': 'Spinifex Underground',
  'Western Tiers Mining Services': 'Western Tiers Mining',
};

/** Demo line 1486. A person's organisation, as the register spells it. */
export const orgOfPerson = (person: Pick<PersonRecord, 'org'>): string =>
  PERSON_ORG_ALIAS[person.org] ?? person.org;

/**
 * The fleet register's spelling of a contractor. See the file header for why
 * this covers both contractors and the demo covers one.
 */
const CONTRACTOR_FLEET_NAME: Readonly<Record<string, string>> = {
  'Spinifex Underground': 'Spinifex Underground Contracting',
  'Western Tiers Mining': 'Western Tiers Mining Services',
};

/** Demo line 2744. Every organisation except the OEM, in register order. */
export const customerOrgs = (): string[] =>
  Object.keys(ORG_REGISTER).filter((org) => org !== OEM_ORG);

/** The site an organisation belongs to — as owner first, else as contractor. */
export function mineOfOrg(org: string): MineCode | undefined {
  const owned = MINE_ORDER.find((m) => ORG_OF_MINE[m] === org);
  if (owned) return owned;
  return MINE_ORDER.find((m) => CONTRACTOR_ORG[m] === org);
}

/** True when the organisation is a mining contractor rather than the owner. */
export const isContractor = (org: string): boolean =>
  Object.values(CONTRACTOR_ORG).includes(org);

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

/** What kind of thing happened. The demo's six `kind` values. */
export type NotificationKind = 'Alert' | 'Parts' | 'Job done' | 'Bulletin' | 'Incident' | 'ETA';

/** Demo line 2736. One drafted customer notification. */
export interface Notification {
  /** Sort key and display timestamp, `YYYY-MM-DDTHH:MM`. */
  readonly at: string;
  /** Organisation it is addressed to, or `'*'` for every site. */
  readonly org: string;
  readonly kind: NotificationKind;
  readonly text: string;
  /** Named recipient, `''` where the source row carried none. */
  readonly to: string;
  /** Stable React key: `at` repeats across sites, `org` repeats across rows. */
  readonly key: string;
}

/** The payloads {@link notifications} joins. */
export interface NotificationCtx {
  readonly orders: readonly PartOrder[];
  readonly techJobs: readonly TechnicianJob[];
  readonly bulletins: readonly Bulletin[];
  readonly techs: readonly TechnicianPresence[];
}

/** Demo line 2737. An alert's display date back into an ISO timestamp. */
const alertAt = (alert: Pick<Alert, 'raised'>): string =>
  `2026-09-${alert.raised.slice(0, 2)}T${alert.raised.slice(7)}`;

/**
 * Demo line 2740. The three bulletins carry a hand-written issue time keyed on
 * the bulletin id; everything else falls to the same default the demo uses.
 */
const BULLETIN_AT: Readonly<Record<string, string>> = {
  'TB-2026-033': '2026-09-07T09:20',
  'SB-2026-004': '2026-09-09T05:30',
};
const BULLETIN_AT_DEFAULT = '2026-09-08T09:00';

/** Demo line 2741. The two reported incidents carry hand-written issue times. */
const INCIDENT_AT: Readonly<Record<string, string>> = {
  'IR-2026-014': '2026-09-07T09:20',
};
const INCIDENT_AT_DEFAULT = '2026-09-08T10:30';

/**
 * Demo line 2736. `notifications()` — everything the Customer relations agent
 * has drafted this fortnight, newest first.
 *
 * Six sources in the demo's order: open alerts, parts orders from 03 Sep,
 * technician-app jobs completed from 01 Sep, bulletins, incidents (excluding
 * the closed Kalbarri one), and the technicians currently on a job.
 */
export function notifications(ctx: NotificationCtx): Notification[] {
  const out: Notification[] = [];
  const push = (n: Omit<Notification, 'key'>, key: string) => out.push({ ...n, key });

  ALERTS.forEach((a) => {
    const mine = BY_ID[a.machine].mine;
    push(
      {
        at: alertAt(a),
        org: ORG_OF_MINE[mine],
        kind: 'Alert',
        text: `${a.machine} — ${a.title}. ${
          a.chain.some((c) => c.status === 'approve')
            ? 'Drafts waiting for approval.'
            : 'No action needed from site.'
        }`,
        to: MINES[mine].planner,
      },
      `alert:${a.id}`,
    );
  });

  ctx.orders
    .filter((o) => o.order_date >= '2026-09-03')
    .forEach((o) =>
      push(
        {
          at: `${o.order_date}T08:00`,
          org: ORG_OF_MINE[o.mine],
          kind: 'Parts',
          text: `${o.order_no} · ${o.part_no} for ${o.machine_id}: ${o.status}`,
          to: MINES[o.mine].planner,
        },
        `order:${o.order_no}`,
      ),
    );

  ctx.techJobs
    .filter((j) => j.status === 'Completed' && j.off_site != null && j.off_site >= '2026-09-01')
    .forEach((j) => {
      const mine = MINE_ORDER.find((m) => MINES[m].name === j.site);
      push(
        {
          at: j.off_site as string,
          org: mine ? ORG_OF_MINE[mine] : '',
          kind: 'Job done',
          text: `${j.job_id} on ${j.machine} completed by ${j.technician}; service report and photos in technician app; CSS survey sent.`,
          to: j.customer_signoff ? j.customer_signoff.name : '',
        },
        `job:${j.job_id}`,
      );
    });

  ctx.bulletins.forEach((b) =>
    push(
      {
        at: BULLETIN_AT[b.id] ?? BULLETIN_AT_DEFAULT,
        org: '*',
        kind: 'Bulletin',
        text: `${b.id} issued — ${b.title}. Acknowledgement requested against each affected serial.`,
        to: 'all superintendents',
      },
      `bulletin:${b.id}`,
    ),
  );

  INCIDENTS.filter((i) => i.id !== 'WO-26-11842').forEach((i) => {
    const mine = i.mine as MineCode;
    push(
      {
        at: INCIDENT_AT[i.id] ?? INCIDENT_AT_DEFAULT,
        org: ORG_OF_MINE[mine],
        kind: 'Incident',
        text: `${i.id} report issued for ${i.machine} — ${i.title}.`,
        to: MINES[mine].maint_super.split(' (')[0],
      },
      `incident:${i.id}`,
    );
  });

  ctx.techs
    .filter((t) => t.current_job)
    .forEach((t, index) =>
      push(
        {
          // The demo indexes into the unfiltered roster; this indexes the
          // filtered list. Both produce a spread of 05:10, 05:17, 05:24 … on
          // the same morning, and the value is decorative.
          at: `2026-09-09T05:${String(10 + index * 7).padStart(2, '0')}`,
          org: ORG_OF_MINE[t.base],
          kind: 'ETA',
          text: `${t.technician} on site for ${t.current_job} — ${
            t.status === 'On site' ? 'on the job' : 'travelling'
          }, last seen ${t.last_seen.slice(11)}.`,
          to: MINES[t.base] ? MINES[t.base].planner : '',
        },
        `eta:${t.technician}`,
      ),
    );

  return out.sort((a, b) => b.at.localeCompare(a.at));
}

/** Demo line 2756. The notification log under the cards. */
export const NOTIFICATION_LOG_LIMIT = 40;

/** Demo line 2751. Notifications shown on one customer's card. */
export const CARD_NOTIFICATION_LIMIT = 5;

// ---------------------------------------------------------------------------
// The cards
// ---------------------------------------------------------------------------

/** Demo line 2749. The three sub-scores behind an overall CSS figure. */
export interface CssBreakdown {
  readonly overall: number | null;
  readonly quality: number | null;
  readonly responsiveness: number | null;
  readonly reporting: number | null;
  readonly responses: number;
}

/** Demo line 2745. One customer organisation's card. */
export interface CustomerCard {
  readonly org: string;
  readonly record: OrgRecord;
  /** The site the organisation is attached to. */
  readonly mine: MineCode;
  readonly siteName: string;
  readonly contractor: boolean;
  /** `'Contractor · Gawler Deep'` — the pill on the card header. */
  readonly relation: string;
  readonly fleet: readonly Machine[];
  /** Machines at an owner's site that a contractor operates. `0` on a contractor. */
  readonly contractorShare: number;
  readonly veryPoorOrWorse: number;
  /** Drafted work orders on this fleet. Carried as rows so the drawer is real. */
  readonly drafts: readonly WorkOrder[];
  readonly openCases: readonly ServiceCase[];
  readonly alerts: readonly Alert[];
  readonly css: CssBreakdown;
  /** The CSS responses the scores were averaged over. */
  readonly cssRows: readonly CustomerSatisfactionResponse[];
  readonly contacts: readonly PersonRecord[];
  readonly channel: string;
  readonly maintenancePlan: string;
  readonly notifications: readonly Notification[];
}

/** Everything {@link customerCards} reads. */
export interface CustomerSceneCtx extends NotificationCtx {
  readonly people: readonly PersonRecord[];
  readonly css: readonly CustomerSatisfactionResponse[];
  readonly cases: readonly ServiceCase[];
  readonly workOrders: readonly WorkOrder[];
}

/**
 * Demo line 2752. The maintenance-plan line. Only Spinifex differs; the demo
 * writes the other two branches out and both say the same thing.
 */
const maintenancePlanFor = (org: string): string =>
  org === 'Spinifex Underground'
    ? 'contract plan reconciled to OEM intervals (08 Sep)'
    : 'OEM intervals';

/** Mean of one CSS column over a site's responses, or `null` when there are none. */
const cssMean = (
  rows: readonly CustomerSatisfactionResponse[],
  key: keyof CustomerSatisfactionResponse,
): number | null =>
  rows.length ? rows.reduce((a, r) => a + (Number(r[key]) || 0), 0) / rows.length : null;

/**
 * Demo line 2745. One card per customer organisation, in register order.
 *
 * Expensive by construction — nine organisations × a `statusOf` pass over their
 * fleet. Call it inside a `useMemo` keyed on the loaded payloads.
 */
export function customerCards(ctx: CustomerSceneCtx): CustomerCard[] {
  const all = notifications(ctx);
  const contractorNames = Object.values(CONTRACTORS);

  return customerOrgs().flatMap((org): CustomerCard[] => {
    const mine = mineOfOrg(org);
    // Every organisation in the register maps to one of the six sites; the
    // guard keeps the type honest rather than guarding against real data.
    if (!mine) return [];

    const contractor = isContractor(org);
    const fleetName = CONTRACTOR_FLEET_NAME[org] ?? org;
    const fleet = FLEET.filter((f) =>
      contractor ? f.operated_by === fleetName : f.mine === mine,
    );
    const ids = new Set(fleet.map((f) => f.machine_id));
    const siteName = MINES[mine].name;
    const css = ctx.css.filter((r) => r.Site === siteName);

    return [
      {
        org,
        record: ORG_REGISTER[org],
        mine,
        siteName,
        contractor,
        relation: `${contractor ? 'Contractor' : 'Owner'} · ${siteName}`,
        fleet,
        contractorShare: contractor
          ? 0
          : fleet.filter((f) => contractorNames.includes(f.operated_by)).length,
        veryPoorOrWorse: fleet.filter((f) =>
          ['major', 'vpoor'].includes(statusOf(f.machine_id)),
        ).length,
        drafts: ctx.workOrders.filter(
          (w) => w.mine === mine && w.status.startsWith('Draft') && ids.has(w.machine_id),
        ),
        openCases: ctx.cases.filter(
          (c) => c.mine === mine && /Open/.test(c.status) && ids.has(c.machine_id),
        ),
        alerts: ALERTS.filter((a) => ids.has(a.machine)),
        cssRows: css,
        css: {
          overall: cssMean(css, 'Overall'),
          quality: cssMean(css, 'Quality (1-5)'),
          responsiveness: cssMean(css, 'Responsiveness (1-5)'),
          reporting: cssMean(css, 'Reporting (1-5)'),
          responses: css.length,
        },
        contacts: ctx.people.filter((p) => orgOfPerson(p) === org),
        channel: channelFor(org),
        maintenancePlan: maintenancePlanFor(org),
        notifications: all
          .filter((n) => n.org === org || n.org === '*')
          .slice(0, CARD_NOTIFICATION_LIMIT),
      },
    ];
  });
}

/** Demo line 2744. The count chip in the page header. */
export interface CustomerSummary {
  readonly organisations: number;
  /** Mine operations Meridian sells to — what the sidebar badge counts. */
  readonly owners: number;
  /** Contracting firms working on a customer's site under that contract. */
  readonly contractors: number;
  readonly notifications: number;
  /** Machines across every customer fleet, counted once each. */
  readonly machines: number;
  readonly attention: number;
}

/** Demo line 2744. The header chip, derived from the cards. */
export function customerSummary(
  cards: readonly CustomerCard[],
  all: readonly Notification[],
): CustomerSummary {
  const seen = new Set<string>();
  cards.forEach((c) => c.fleet.forEach((f) => seen.add(f.machine_id)));
  return {
    organisations: cards.length,
    owners: cards.filter((c) => !c.contractor).length,
    contractors: cards.filter((c) => c.contractor).length,
    notifications: all.length,
    machines: seen.size,
    attention: [...seen].filter((id) => ATTENTION_BANDS.includes(statusOf(id))).length,
  };
}
